import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  loginApi,
  logoutApi,
  apiFetch as apiFetchUtil,
  getStoredUser,
  getStoredTokens,
  getStoredClient,
  getStoredPermissions,
  STORAGE_KEYS,
} from '../utils/api';


const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [clientData, setClientData] = useState(null);
  const [user, setUser] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load stored data on mount
  useEffect(() => {
    const stored = getStoredClient();
    if (stored) setClientData(stored);
    const u = getStoredUser();
    if (u) setUser(u);
    const t = getStoredTokens();
    if (t) setTokens(t);
    const p = getStoredPermissions();
    if (p) setPermissions(p);
    setIsLoading(false);
  }, []);

  const isAuthenticated = !!user && !!tokens?.access_token;

  const isAdmin = user?.role_slug === 'master' || user?.role_slug === 'admin';
  const isSalesManager = user?.role_slug === 'sales_manager';
  const isTeamLeader = user?.role_slug === 'team_leader';

  const hasPermission = useCallback((code) => {
    if (!user) return false;
    if (user.role_slug === 'master' || user.role_slug === 'admin') return true;
    if (!code) return true;
    return Array.isArray(permissions) && permissions.includes(code);
  }, [user, permissions]);

  const login = useCallback(async (username, password) => {
    const result = await loginApi(username, password);
    if (result.success) {
      setUser(result.data.user);
      setTokens(result.data.tokens);
      setPermissions(result.data.permissions || []);
    }
    return result;
  }, []);

  // Merge a partial update into the in-memory user and persist to localStorage,
  // so sidebar/header avatar & name update live without a full reload.
  const updateUserData = useCallback((partial) => {
    if (!partial) return;
    setUser(prev => {
      const next = { ...(prev || {}), ...partial };
      try { localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    // Fire-and-forget; UI clears immediately, backend cleanup happens in bg
    logoutApi();
    setUser(null);
    setTokens(null);
    setPermissions([]);
  }, []);

  const apiFetch = useCallback(async (endpoint, options = {}) => {
    return apiFetchUtil(endpoint, options);
  }, []);

  const refreshingRef = useRef(false);
  const refreshPermissions = useCallback(async () => {
    if (refreshingRef.current) return;
    const t = getStoredTokens();
    if (!t?.access_token) return;
    refreshingRef.current = true;
    try {
      const res = await apiFetchUtil('auth/me');
      if (res.success && Array.isArray(res.data?.permissions)) {
        setPermissions(res.data.permissions);
        try { localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(res.data.permissions)); } catch {}
        if (res.data.user?.is_active === 0) {
          logoutApi();
          setUser(null);
          setTokens(null);
          setPermissions([]);
        }
      }
    } catch {}
    refreshingRef.current = false;
  }, []);

  useEffect(() => {
    if (!user || !tokens?.access_token) return;
    const onFocus = () => refreshPermissions();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshPermissions(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(refreshPermissions, 60000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [user, tokens, refreshPermissions]);


  // Accounts seeded at signup start with a temporary password and must set a
  // real one before they can use the CRM.
  const mustChangePassword = !!user?.must_change_password;
  const clearMustChangePassword = useCallback(
    () => updateUserData({ must_change_password: 0 }),
    [updateUserData]
  );

  const value = {
    clientData, user, tokens, permissions,
    isLoading, isAuthenticated,
    isAdmin, isSalesManager, isTeamLeader,
    mustChangePassword, clearMustChangePassword,
    hasPermission,
    login, logout, apiFetch, refreshPermissions, updateUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
